import { z } from 'zod';
import { compareByRank, type SortDir } from './crew.js';
import type {
  CertificateInput,
  CertificateRecord,
  CertificateStatus,
  CrewCertificates,
  CrewMember,
  PersonnelVessel,
} from './types.js';

/** Katalog kategorileri, ekrandaki sırayla (müşteri ekranı "Certificate Categories"). */
export const CERTIFICATE_CATEGORIES = [
  'competency',
  'gmdss',
  'stcw_safety',
  'security',
  'medical',
  'specialized',
  'personal',
  'other',
] as const;
export type CertificateCategory = (typeof CERTIFICATE_CATEGORIES)[number];

export type RowStatus = CertificateStatus | 'missing';
export type StatusFilter = 'all' | RowStatus;
export const CERTIFICATE_PAGE_SIZE = 12;

/** Sol sütun: gemiler ve gemide olanların sayısı; karadakiler ayrı (`ashore`) grupta. */
export interface VesselGroup {
  id: string;
  /** `ashore` grubunda `null`; adı arayüz çevirir. */
  name: string | null;
  count: number;
}

export function vesselGroups(
  vessels: readonly PersonnelVessel[],
  crew: readonly CrewMember[],
  locale: string,
): VesselGroup[] {
  const onBoard = (vesselId: string) =>
    crew.filter((m) => m.status === 'on_board' && m.vessel?.id === vesselId).length;
  return [
    ...[...vessels]
      .sort((x, y) => x.name.localeCompare(y.name, locale))
      .map((vessel) => ({ id: vessel.id, name: vessel.name, count: onBoard(vessel.id) })),
    // "Shore / Available": karadakiler ve yalnız kartı açılmış (ataması olmayan) kişiler
    { id: 'ashore', name: null, count: crew.filter((m) => m.status !== 'on_board').length },
  ];
}

/** Seçili gruptaki kişiler, rütbe kıdemiyle sonra ada göre (M6f G2-01); arama ad ve rütbede. */
export function crewOfGroup(
  crew: readonly CrewMember[],
  groupId: string,
  query: string,
  locale: string,
): CrewMember[] {
  const q = query.trim().toLocaleLowerCase(locale);
  return crew
    .filter((m) =>
      groupId === 'ashore'
        ? m.status !== 'on_board'
        : m.status === 'on_board' && m.vessel?.id === groupId,
    )
    .filter((m) => !q || `${m.fullName} ${m.role ?? ''}`.toLocaleLowerCase(locale).includes(q))
    .sort((x, y) => compareByRank(x, y, locale));
}

/** Tablo satırı: kayıtlı belge ya da kaydı olmayan zorunlu tür (`record` boş). */
export interface CertificateRow {
  key: string;
  typeId: string;
  code: string;
  name: string;
  category: string;
  status: RowStatus;
  record: CertificateRecord | null;
}

const ORDER: Record<RowStatus, number> = { expired: 0, expiring: 1, valid: 2, missing: 3 };

/**
 * Varsayılan belge kataloğunun sırası (`packages/db/src/seed.ts` `DEFAULT_CERTIFICATE_TYPES`;
 * müşteri ekranı `personel-sertifikalar.jpeg`: BFF, PST, AFF). Kayıt kataloğun sırasını taşımaz;
 * şirketin eklediği türler kategorisinde bunlardan sonra, adıyla gelir.
 */
export const CATALOG_ORDER = [
  'COC',
  'COOK',
  'GOC',
  'BFF',
  'PST',
  'AFF',
  'EFA',
  'PSSR',
  'MFA',
  'MED',
  'YF',
  'SA',
  'SSO',
  'RO',
  'TF',
  'PASSPORT',
  'SBOOK',
] as const;

export type CertificateSortKey = 'name' | 'category' | 'issued' | 'expires' | 'status';
export interface CertificateSort {
  key: CertificateSortKey;
  dir: SortDir;
}

const categoryIndex = (category: string) => {
  const index = (CERTIFICATE_CATEGORIES as readonly string[]).indexOf(category);
  return index === -1 ? CERTIFICATE_CATEGORIES.length - 1 : index;
};
const catalogIndex = (code: string) => {
  const index = (CATALOG_ORDER as readonly string[]).indexOf(code);
  return index === -1 ? CATALOG_ORDER.length : index;
};

/**
 * Tablo satırları. Varsayılan sıra katalog sırasıdır (M6f G2-18; ADR 0018 §4): kategori
 * ("Certificate Categories" kartının sırası), kategoride katalog sırası, sonra ad; kaydı olmayan
 * zorunlu türler en sonda. `sort` verilirse sütuna göre, iki yönlü; tarihi ya da kategorisi
 * olmayan satır her iki yönde de sonda kalır.
 */
export function certificateRows(
  data: CrewCertificates,
  options: { status: StatusFilter; query: string; locale: string; sort?: CertificateSort | null },
): CertificateRow[] {
  const q = options.query.trim().toLocaleLowerCase(options.locale);
  const rows: CertificateRow[] = [
    ...data.certificates.map((record) => ({
      key: record.id,
      typeId: record.typeId,
      code: record.code,
      name: record.name,
      category: record.category,
      status: record.status as RowStatus,
      record,
    })),
    ...data.summary.missing.map((type) => ({
      key: `missing-${type.typeId}`,
      typeId: type.typeId,
      code: type.code,
      name: type.name,
      category: '',
      status: 'missing' as const,
      record: null,
    })),
  ];
  const byName = (x: CertificateRow, y: CertificateRow) =>
    x.name.localeCompare(y.name, options.locale);
  const catalog = (x: CertificateRow, y: CertificateRow) =>
    Number(x.record === null) - Number(y.record === null) ||
    categoryIndex(x.category) - categoryIndex(y.category) ||
    catalogIndex(x.code) - catalogIndex(y.code) ||
    byName(x, y);
  const sort = options.sort ?? null;
  const sign = sort?.dir === 'desc' ? -1 : 1;
  /** Boş değer her iki yönde sonda; doluysa yönle karşılaştırılır. */
  const lastIfEmpty = <T>(a: T | null, b: T | null, compare: (a: T, b: T) => number) =>
    a === null || b === null ? Number(a === null) - Number(b === null) : sign * compare(a, b);
  const byDate =
    (pick: (record: CertificateRecord) => string | null) =>
    (x: CertificateRow, y: CertificateRow) =>
      lastIfEmpty(x.record ? pick(x.record) : null, y.record ? pick(y.record) : null, (a, b) =>
        a.localeCompare(b),
      );
  const compare: Record<CertificateSortKey, (x: CertificateRow, y: CertificateRow) => number> = {
    name: (x, y) => sign * byName(x, y),
    category: (x, y) =>
      lastIfEmpty(
        x.category || null,
        y.category || null,
        (a, b) => categoryIndex(a) - categoryIndex(b),
      ),
    issued: byDate((record) => record.issuedOn),
    expires: byDate((record) => record.expiresOn),
    status: (x, y) => sign * (ORDER[x.status] - ORDER[y.status]),
  };
  return rows
    .filter((row) => options.status === 'all' || row.status === options.status)
    .filter(
      (row) =>
        !q ||
        [row.name, row.code, row.record?.number ?? '']
          .join(' ')
          .toLocaleLowerCase(options.locale)
          .includes(q),
    )
    .sort((x, y) => (sort ? compare[sort.key](x, y) || catalog(x, y) : catalog(x, y)));
}

export function categoryCounts(
  certificates: readonly CertificateRecord[],
): Record<CertificateCategory, number> {
  const counts = Object.fromEntries(CERTIFICATE_CATEGORIES.map((c) => [c, 0])) as Record<
    CertificateCategory,
    number
  >;
  for (const certificate of certificates) {
    const category = (CERTIFICATE_CATEGORIES as readonly string[]).includes(certificate.category)
      ? (certificate.category as CertificateCategory)
      : 'other';
    counts[category] += 1;
  }
  return counts;
}

/** Bitişe kalan gün; belgede yazan bitiş günü hâlâ geçerlidir (0). Süresiz belgede `null`. */
export function daysLeft(expiresOn: string | null, today: string): number | null {
  if (expiresOn === null) return null;
  return Math.round((Date.parse(expiresOn) - Date.parse(today)) / 86_400_000);
}

const optionalText = (max: number) => z.string().trim().max(max);
const optionalDate = z.union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]);

/**
 * Belge formu: sunucudaki kuralların arayüzdeki karşılığı; sunucu yine kendisi doğrular
 * (`EXPIRES_BEFORE_ISSUED`). Boş alan "bilinmiyor" ya da "süresiz" demektir.
 */
export const certificateSchema = z
  .object({
    certificateTypeId: z.string().min(1, 'certificates.form.typeRequired'),
    number: optionalText(100),
    issuedOn: optionalDate,
    expiresOn: optionalDate,
    issuedBy: optionalText(200),
  })
  .refine((v) => !v.issuedOn || !v.expiresOn || v.expiresOn >= v.issuedOn, {
    path: ['expiresOn'],
    message: 'errors.EXPIRES_BEFORE_ISSUED',
  });
export type CertificateValues = z.infer<typeof certificateSchema>;

export function toCertificateInput(values: CertificateValues): CertificateInput {
  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());
  return {
    certificateTypeId: values.certificateTypeId,
    number: orNull(values.number),
    issuedOn: orNull(values.issuedOn),
    expiresOn: orNull(values.expiresOn),
    issuedBy: orNull(values.issuedBy),
  };
}
